import {
  Alert,
  AlertDescription,
  AlertIcon,
  Avatar,
  Button,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { React, useMemo } from 'react';
import { HEADER_MIN_HEIGHT } from '../appTheme';
import {
  useCurrentHotel,
  useHandleOtherHotelForHotelGroupChange,
  useOtherHotelForHotelGroup,
  usePreloadHotel,
} from '../hooks/currentHotel';
import HotelSelect from '../../core/components/Select/HotelSelect';
import {
  useCurrentUser,
  useOnUserDisconnection,
} from '../hooks/authenticationStatus';
import { useRouter } from 'next/router';
import useApiClient from '../hooks/apiClient';
import { Roles } from '../enum/Roles';
import getConfig from 'next/config';

const envConfig = getConfig().publicRuntimeConfig;

export default function Header(props) {
  const hotel = useCurrentHotel();
  const user = useCurrentUser();
  const router = useRouter();
  const apiClient = useApiClient();
  const onUserDisconnection = useOnUserDisconnection();

  const hotelSelectValue = useMemo(() => {
    return {
      id: hotel?.id,
      label: hotel?.name,
    };
  }, [hotel]);

  const otherHotelForHotelGroup = useOtherHotelForHotelGroup();
  const isMultiPropertyView = !!useOtherHotelForHotelGroup();

  const otherHotelForHotelGroupValue = useMemo(() => {
    if (!otherHotelForHotelGroup) {
      return null;
    }
    return {
      id: otherHotelForHotelGroup.id,
      label: otherHotelForHotelGroup.name,
    };
  }, [otherHotelForHotelGroup]);

  const handleOtherHotelForHotelGroupChange =
    useHandleOtherHotelForHotelGroupChange();

  const preloadHotel = usePreloadHotel();

  const handleHotelChange = (selectedValue) => {
    const newSlug = selectedValue.item.slug;
    if (hotel) {
      preloadHotel(selectedValue.item);
      const cleanPath = router.asPath.split('?')[0];
      const newPath = cleanPath.replace(`/${hotel.slug}`, `/${newSlug}`);
      router.push(newPath);
      return;
    }

    router.push(`/${newSlug}/contrats`);
  };

  return (
    <Flex
      id="header"
      ref={props.header}
      background={'light'}
      px={4}
      minHeight={HEADER_MIN_HEIGHT}
      borderBottomWidth={1}
      alignItems="center"
      justifyContent="space-between"
      pos="sticky"
      top={0}
      zIndex={2}
    >
      <Heading
        title={props.pageTitle}
        color="primaryLighter"
        fontSize="3xl"
        noOfLines={1}
      >
        {props.pageTitle}
      </Heading>

      {envConfig.APP_ENV !== 'production' && (
        <Alert status="info" maxW={96} py={1.5} fontSize={14}>
          <AlertIcon boxSize={4} />
          <AlertDescription>
            Environnement :{' '}
            <strong>
              {envConfig.APP_ENV !== 'dev' ? 'Préproduction' : 'Développement'}
            </strong>
          </AlertDescription>
        </Alert>
      )}

      <Flex>
        <Flex mx={4} alignItems="center">
          <HotelSelect
            mb={0}
            size="xs"
            label={null}
            allowClear={false}
            id="hotelSelector"
            selectedItem={hotelSelectValue}
            includes={[
              'hasLibeoAPIConnexion',
              'hasExthandActivated',
              'hotelGroup',
              'bankAccounts',
              'bankAccounts.bankConnection',
              'bankAccounts.exthandBankAccount',
              'bankAccounts.manualBankAccount',
              'bankAccounts.termBankAccount',
              'bankAccounts.hotel',
            ]}
            onChange={handleHotelChange}
            mr={hotel && hotel.hotelGroup ? 2 : 0}
          />
          {hotel && hotel.hotelGroup && (
            <HotelSelect
              mb={0}
              size="xs"
              label={null}
              allowClear={true}
              id="multiHotelSelector"
              selectedItem={otherHotelForHotelGroupValue}
              includes={[
                'hasLibeoAPIConnexion',
                'hasExthandActivated',
                'bankAccounts',
                'bankAccounts.bankConnection',
                'bankAccounts.exthandBankAccount',
                'bankAccounts.manualBankAccount',
                'bankAccounts.termBankAccount',
                'bankAccounts.hotel',
              ]}
              onChange={(value) =>
                handleOtherHotelForHotelGroupChange(value?.item)
              }
              placeholder="Navigation multi-hôtels"
              filters={{
                hotelGroup: hotel.hotelGroup.id,
                notIds: hotel.id,
              }}
            />
          )}
        </Flex>

        <Menu closeOnSelect={false}>
          <MenuButton as={Button} variant="unstyled">
            <Flex
              as="span"
              display="flex"
              alignItems="center"
              px={2}
              whiteSpace="nowrap"
            >
              <Avatar bg="primaryLighter" mr={1} size="xs" />
              {user.firstName} {user.lastName}
            </Flex>
          </MenuButton>
          <MenuList zIndex={1000}>
            {user?.roles.includes(Roles.ROLE_ADMINISTRATOR) && (
              <>
                <MenuItem onClick={() => router.push('/gestion/utilisateurs')}>
                  Gestion des utilisateurs
                </MenuItem>
                {!isMultiPropertyView && (
                  <MenuItem onClick={() => router.push('/gestion/exthand')}>
                    Exthand
                  </MenuItem>
                )}
                <MenuDivider />
              </>
            )}
            <MenuItem
              onClick={async () => {
                await apiClient('/api/logout');

                onUserDisconnection();
              }}
            >
              Déconnexion
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </Flex>
  );
}
